import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Popover, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { ArrowDropDown as ArrowDropDownIcon, ArrowDropUp as ArrowDropUpIcon } from '@mui/icons-material/';
import { UserPreference } from "../../interfaces/UserPreference";
import useUser from "../../hooks/useUser";
import { useDispatch } from "react-redux";
import { useErrorBoundary } from "react-error-boundary";
import PreferencesService from "../../services/preferences.service";
import classes from "./PreferencesDropdown.module.scss";
import StringHelper from "../../helpers/string.helper";
import PreferenceContext from "../../context/PreferenceContext";
import SubscribeButton from "../../views/subscribe-button/SubscribeButton";
import Notification from "../../views/notification/Notification";
import { NotificationSeverities } from "../../enums/NotificationSeverities";
import { TipFilters } from "../../interfaces/TipFilters";
import { resetAllTipFilters, setAllTipFilters } from "../../../store/filters/tipFilters.slice";
import { Author } from "../../interfaces/Author";
import { ContentLengthOptions } from "../../enums/ContentLengthOptions";

const TipPreferencesDropdown = () => {
  const [preferences, setPreferences] = useState<UserPreference[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const { preferenceID, setPreferenceID } = useContext(PreferenceContext);
  const user = useUser();
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();

  const fetchPreferences = useCallback(() => {
    if (user?.email) {
      PreferencesService.getByUserEmail(user.email)
        .then((preferences) => {
          setPreferences(preferences.filter((preference) => preference.isForTipCollectionType === true));
          const existingPreference = preferences.find((preference) => preference.id === preferenceID);

          if (existingPreference) {
            setPreferenceID(existingPreference.id);
          }
        })
        .catch((error) => showBoundary(error));
    }
  }, [showBoundary, preferenceID, setPreferenceID, user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const clearPreference = useCallback(() => {
    setPreferenceID(0);
    dispatch(resetAllTipFilters());
  }, [dispatch, setPreferenceID]);

  const toggleHandler = useCallback((id: number, checked: boolean) => {
    if (checked) {
      setPreferenceID(id);
      const preference = preferences.find((preference) => preference.id === id);

      if (preference) {
        const tags = preference.tags.map((tag) => ({
          id: tag.id,
          attributes: { name: tag.name, type: tag.type, tagger_id: tag.tagger_id, sport: tag.sport, gender: tag.gender },
        }));

        const authors = preference.authors.map((author) => ({
          id: author.id,
          attributes: { name: author.name, email: author.email, metadata: author.metadata },
        }));

        const filters: TipFilters = {
          hasWords: null,
          tags: tags,
          date: null,
          authors: authors as Author[],
          contentLength: preference.contentLength ?? ContentLengthOptions.LONG,
        };
        dispatch(setAllTipFilters(filters));
      }
    } else {
      clearPreference();
    }
  }, [dispatch, preferences, clearPreference, setPreferenceID]);

  const buttonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const renderPreferences = useMemo(() => {
    const onSubscribeButtonSuccessHandler = (isSubscribed: boolean) => {
      setNotificationMessage(`Successfully ${!isSubscribed ? 'subscribed!' : 'unsubscribed!'}`);
      setIsNotificationOpen(true);
      fetchPreferences();
    };

    return <TableContainer>
      <Table>
        <TableHead className='my-5'>
          <TableRow>
            <TableCell key={StringHelper.generateRandomString(10)}>
              <Typography className={classes['header-text']}>Tags</Typography>
            </TableCell>
            <TableCell key={StringHelper.generateRandomString(10)}>
              <Typography className={classes['header-text']}>Authors</Typography>
            </TableCell>
            <TableCell key={StringHelper.generateRandomString(10)}>
              <Typography className={classes['header-text']}>Tip Length</Typography>
            </TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preferences.map((preference) => (
            <TableRow key={preference.id}>
              <TableCell className={`${classes['cell']} py-1`} >
                <Typography className={`fw-bold ${classes['cell-text']}`}>
                  {preference.tags.map((tag) => (`${tag.name}`)).join(' | ')}
                </Typography>
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <Typography className={`fw-bold ${classes['cell-text']}`}>
                  {preference.authors.map((author) => author.name).join(' | ')}
                </Typography>
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <Typography className={`fw-bold ${classes['cell-text']}`}>
                  {preference.contentLength}
                </Typography>
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <Switch
                  classes={{ checked: 'switch', track: 'switch-base' }}
                  edge='start'
                  checked={preferenceID === preference.id}
                  onChange={(event, checked) => toggleHandler(preference.id, checked)}
                />
              </TableCell>
              <TableCell className={`${classes['cell']} py-1`} >
                <SubscribeButton preference={preference} onSuccess={() => onSubscribeButtonSuccessHandler(preference.receiveNotifications)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  }, [preferences, preferenceID, toggleHandler, fetchPreferences]);

  return <>
    {preferences.length > 0 &&
      <>
        <Box className='ms-5 ms-sm-0'>
          <Button
            className='filter-button'
            onClick={buttonClickHandler}
            endIcon={anchorEl ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          >
            Saved filters
            <Box className={`${classes['filters-number']} ms-2`}>
              <Typography textAlign='center' fontSize='12px' fontWeight='400px'>
                {preferences.length}
              </Typography>
            </Box>
          </Button>
        </Box>
        <Popover
          className='my-2'
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {renderPreferences}
        </Popover>
      </>
    }
    <Notification
      open={isNotificationOpen}
      onClose={() => setIsNotificationOpen(false)}
      message={notificationMessage}
      severity={NotificationSeverities.SUCCESS}
      position={{ vertical: 'top', horizontal: 'right' }}
    />
  </>
};

export default TipPreferencesDropdown;
